import axiosClient from './axiosClient';
import { User, UserRole, PagedResponse } from '@/types/common';

export interface UserFilters {
  page?: number;
  size?: number;
  search?: string;
  role?: UserRole;
  isActive?: boolean;
}

export interface CreateUserData {
  fullName: string;
  username: string;
  email: string;
  role: UserRole;
  isActive?: boolean;
  reasonForAccess?: string;
}

export interface CreateUserResponse extends User {
  tempPassword?: string;
}

export const userApi = {
  createUser: async (data: CreateUserData): Promise<CreateUserResponse> => {
    const response = await axiosClient.post('/users', data);
    return response.data;
  },

  getAll: async (params?: UserFilters): Promise<PagedResponse<User>> => {
    const response = await axiosClient.get('/users', { params });
    return response.data;
  },

  getById: async (id: string): Promise<User> => {
    const response = await axiosClient.get(`/users/${id}`);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await axiosClient.get('/users/me');
    return response.data;
  },

  updateUser: async (id: string, data: Partial<User>): Promise<User> => {
    const response = await axiosClient.put(`/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: string): Promise<void> => {
    await axiosClient.delete(`/users/${id}`);
  },

  permanentDeleteUser: async (id: string): Promise<void> => {
    await axiosClient.delete(`/users/${id}/permanent`);
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await axiosClient.put('/users/me/password', { currentPassword, newPassword });
  },

  updateMyTheme: async (uiThemePreference: 'LIGHT' | 'DARK' | 'SYSTEM'): Promise<User> => {
    const response = await axiosClient.put('/users/me/theme', { uiThemePreference });
    return response.data;
  },

  getUserNamesByRole: async (role: UserRole): Promise<{ id: string; fullName: string }[]> => {
    const response = await axiosClient.get(`/users/by-role/${role}`);
    return response.data;
  },

  checkUsername: async (username: string): Promise<boolean> => {
    const response = await axiosClient.get<{ success: boolean; data: { exists: boolean } }>(
      '/auth/check-username',
      { params: { username } }
    );
    return response.data.data.exists;
  },
};
